import { Account, AccountUser } from "@mixitone/models";
import { debounce } from "@mixitone/util";
import { AppController } from "controllers/AppController";
import MixitoneController from "controllers/MixitoneController";
import RouteController from "controllers/RouteController";
import { globalSearch } from "lib/globalSearch";
import { navigateToGlobalSearchResult } from "lib/globalSearchNavigation";
import {
  GLOBAL_SEARCH_MIN_LENGTH,
  groupGlobalSearchResults,
  type GlobalSearchRankingOptions,
  type GlobalSearchResult,
  type GlobalSearchResultType,
} from "lib/globalSearchTypes";

interface State {
  /** Debounced query used for the active search request. */
  query: string;
  /** Set when a search request finishes so the UI can show an empty state. */
  completedQuery: string;
  results: GlobalSearchResult[];
  loading: boolean;
  groupedResults: Array<{ type: GlobalSearchResultType; label: string; results: GlobalSearchResult[] }>;
  /** Reveal the collapsed header search after Cmd/Ctrl+K. */
  forceVisible: boolean;
}

const GLOBAL_SEARCH_INPUT_SELECTOR = '[data-testid="global-search-input"]';

export default class GlobalSearchController extends MixitoneController<State> {
  private searchRequestId = 0;
  private lastSearchedClubId: string | null = null;
  private pendingSearch = "";
  private readonly debouncedSearch = debounce((search: string) => {
    this.state.query = search;
    void this.runSearch(search);
  }, 250);

  get initialState(): State {
    return {
      query: "",
      completedQuery: "",
      results: [],
      loading: false,
      groupedResults: [],
      forceVisible: false,
    };
  }

  override async initialize() {
    let app: AppController | null = null;
    try {
      app = this.get(AppController);
      app.registerGlobalSearchFocusHandler(() => this.actionFocus());
      this.addDependency(() => app?.registerGlobalSearchFocusHandler(null));

      this.observe(() => {
        if (!app || app.state.pendingGlobalSearchFocus <= 0) return;
        app.consumePendingGlobalSearchFocus();
        this.actionFocus();
      });
    } catch {
      // AppController not in ancestor chain (defensive).
    }

    this.observe(() => {
      this.getSearchRankingOptions();
      this.updateGroupedResults(this.state.results);
    });

    this.observe(() => {
      let clubId: string | null = null;
      try {
        clubId = (app ?? this.get(AppController)).state.currentClub?.id ?? null;
      } catch {
        return;
      }
      if (clubId === this.lastSearchedClubId) return;
      this.lastSearchedClubId = clubId;
      if (this.pendingSearch.length >= GLOBAL_SEARCH_MIN_LENGTH) {
        this.state.query = this.pendingSearch;
        void this.runSearch(this.pendingSearch);
      }
    });
  }

  private getSearchRankingOptions(): GlobalSearchRankingOptions {
    const app = this.get(AppController);
    const route = this.get(RouteController);
    const clubId = app.state.currentClub?.id ?? null;
    const pathname = route.state.location?.pathname ?? "";

    if (/^\/tournament\/[^/]+/.test(pathname) && !pathname.startsWith("/tournament/mockup")) {
      return {
        kind: "tournament",
        clubId,
        tournamentSessionId: route.params.id ?? null,
      };
    }

    return { kind: "club", clubId };
  }

  actionFocus() {
    this.state.forceVisible = true;
  }

  actionBlur() {
    requestAnimationFrame(() => {
      const root = document.querySelector("[data-testid=global-search-root]");
      if (root?.contains(document.activeElement)) return;
      this.state.forceVisible = false;
    });
  }

  actionQueueSearch(search: string) {
    this.pendingSearch = search;

    if (search.length < GLOBAL_SEARCH_MIN_LENGTH) {
      if (this.state.query !== "" || this.state.results.length > 0 || this.state.loading) {
        this.state.query = "";
        this.state.completedQuery = "";
        this.state.results = [];
        this.state.loading = false;
      }
      return;
    }

    this.debouncedSearch(search);
  }

  private includeFunding(): boolean {
    const clubId = this.get(AppController).state.currentClub?.id;
    if (!clubId) return false;
    if (!Account.current?.hasFeature("funding")) return false;
    return AccountUser.current?.clubPermissionFor(clubId).manage_funding === true;
  }

  private async runSearch(search: string) {
    const requestId = ++this.searchRequestId;
    const app = this.get(AppController);
    const clubId = app.state.currentClub?.id;

    this.state.loading = true;

    try {
      const results = await globalSearch({
        search,
        includeFunding: this.includeFunding(),
        ranking: this.getSearchRankingOptions(),
      });

      if (requestId !== this.searchRequestId) return;

      this.lastSearchedClubId = clubId ?? null;
      this.state.results = results;
    } catch (error) {
      console.error("Global search failed", error);
      if (requestId !== this.searchRequestId) return;
      this.state.results = [];
    } finally {
      if (requestId === this.searchRequestId) {
        this.state.loading = false;
        this.state.completedQuery = search;
      }
    }
  }

  private updateGroupedResults(results: GlobalSearchResult[]) {
    this.state.groupedResults = groupGlobalSearchResults(results, this.getSearchRankingOptions());
  }

  async actionSelect(result: GlobalSearchResult | null) {
    if (!result) return;

    await navigateToGlobalSearchResult(result, this);
    this.pendingSearch = "";
    this.state.query = "";
    this.state.completedQuery = "";
    this.state.results = [];
    this.state.loading = false;
    document.querySelector<HTMLInputElement>(GLOBAL_SEARCH_INPUT_SELECTOR)?.blur();
  }
}
